import {
  Action_Type,
  Agent_BuildCacheSettingsInput,
  Agent_SortField,
  Agent_SortOrder,
  Agent_WhereInput,
  License_RegisterAgentRequest,
  License_RegisterBuildCacheRequest,
  Scalars,
  Cluster_Nodes_AddInput,
  Cluster_Nodes_RemoveInput,
  useAgents_Action_AsyncMutation,
  useAgents_BuildCache_Update_AsyncMutation,
  useLicense_RegisterAgents_AsyncMutation,
  useLicense_RegisterBuildCache_AsyncMutation,
  useCluster_Add_NodesMutation,
  useCluster_Remove_NodesMutation,
} from '@/graphql/codegen/generated/graphql';
import { longPolling } from '@/services/PlatfromGQL/longPolling';
import { NoUndefinedField } from '@/types/utils';
import { computed, inject, ref, watch } from 'vue';
import { app } from '@/bootloader';
import { Agent_SearchColumns } from '@/graphql/codegen/gql/graphql';
import { OsType } from '@/modules/platformAuthentication/platformAuthentication.const';
import { useOsService } from '@/services/OsService/OsService';
import { IKApplicationPersistentData } from '@/services/persistentDataProvider/persistentDataProvider.types';
import { IKAgentsColumnsManagement, IKAgentsPersistentData, IKAgentsQueryFilter } from './agents.injectionKeys';
import { useAgentsTableConstants } from './components/agents-table/agents-table.const';
import { AgentsTableWindowsColumns } from './components/agents-table/types/OS/agents-table.windows';
import { AgentsTableLinuxColumns } from './components/agents-table/types/OS/agents-table.linux';
import { TableColumns, TableEditor } from './components/agents-table/types/agents-table.types';

export type AgentsPagePersistentDataType = {
  tableEditor: {
    windows: {
      columnsOrder: TableColumns<OsType.Windows>;
      columnsFilter: Partial<Record<AgentsTableWindowsColumns, boolean>>;
    };
    linux: {
      columnsOrder: TableColumns<OsType.Linux>;
      columnsFilter: Partial<Record<AgentsTableLinuxColumns, boolean>>;
    };
  };
  pagination: {
    pageSize: number;
  };
  filter: {
    windows: {};
    linux: {};
  };
  sorting: {
    sort: {
      field: Agent_SortField;
      order: Agent_SortOrder;
    };
    secondarySortField?: Agent_SortField;
  };
  agentRowCompactView: boolean;
};

export const useAgentsPagePersistentDataProvider = () => {
  const agentsPagePersistentDataSection = 'AgentsPage';
  const appPersistentData = inject(IKApplicationPersistentData);
  const agentsPersistentData = ref({
    tableEditor: {
      windows: {
        columnsOrder: [],
        columnsFilter: {},
      },
      linux: {
        columnsOrder: [],
        columnsFilter: {},
      },
    },
    pagination: {
      pageSize: 50,
    },
    filter: {
      windows: {},
      linux: {},
    },
    sorting: {
      sort: {
        field: '',
        order: '',
      },
      secondarySortField: {},
    },
    agentRowCompactView: false,
  } as any);
  const osService = useOsService();

  watch(
    () => appPersistentData?.GQL?.networkStatus?.value,
    () => {
      const result = appPersistentData?.getPersistentData(agentsPagePersistentDataSection) as AgentsPagePersistentDataType;
      if (result && typeof result === 'object' && Object.keys(result).length > 0) {
        agentsPersistentData.value = result;
      }
    },
    {
      immediate: true,
      deep: true,
    },
  );

  const getTableEditor = computed(() => agentsPersistentData.value?.tableEditor?.[osService?.osType.value || OsType.Windows]);

  const setTableEditor = (value: AgentsPagePersistentDataType['tableEditor']['windows'] | AgentsPagePersistentDataType['tableEditor']['linux']) => {
    agentsPersistentData.value.tableEditor[osService?.osType.value || OsType.Windows] = value;
    appPersistentData?.setPersistentData(agentsPagePersistentDataSection, agentsPersistentData.value);
  };

  const setAgentsTableCompactView = (value: boolean) => {
    agentsPersistentData.value.agentRowCompactView = value;
    appPersistentData?.setPersistentData(agentsPagePersistentDataSection, agentsPersistentData.value);
  };

  const getAgentsTableCompactView = computed(() => agentsPersistentData.value.agentRowCompactView);

  const getSorting = computed(() => agentsPersistentData.value?.sorting);

  const setSorting = (value: AgentsPagePersistentDataType['sorting']) => {
    agentsPersistentData.value.sorting = value;
    appPersistentData?.setPersistentData(agentsPagePersistentDataSection, agentsPersistentData.value);
  };

  const getPaging = computed(() => agentsPersistentData.value?.pagination?.pageSize);

  const setPaging = (value: AgentsPagePersistentDataType['pagination']) => {
    agentsPersistentData.value.pagination = value;
    appPersistentData?.setPersistentData(agentsPagePersistentDataSection, agentsPersistentData.value);
  };

  const getFilter = computed(() => agentsPersistentData.value?.filter?.[osService?.osType.value || OsType.Windows]);

  const setFilter = (value: AgentsPagePersistentDataType['filter']['windows'] | AgentsPagePersistentDataType['filter']['linux']) => {
    agentsPersistentData.value.filter[osService?.osType.value || OsType.Windows] = value;
    appPersistentData?.setPersistentData(agentsPagePersistentDataSection, agentsPersistentData.value);
  };

  const payload = {
    getTableEditor,
    setTableEditor,
    getSorting,
    setSorting,
    getPaging,
    setPaging,
    getFilter,
    setFilter,
    getAgentsTableCompactView,
    setAgentsTableCompactView,
  };

  app.provide(IKAgentsPersistentData, payload);
  return payload;
};

export const useAgentsColumnsManagementProvider = () => {
  const agentsPagePersistentData = inject(IKAgentsPersistentData);

  const osService = useOsService();

  const agentsTableConstants = useAgentsTableConstants();
  const defaultColumnsData = computed(() => {
    const viewableColumns = agentsTableConstants.columns.value.filter(column => agentsTableConstants.viewableColumns.value.has(column.name));

    return {
      columnsFilter: viewableColumns.reduce((acc, cur) => ({ ...acc, [cur.name]: agentsTableConstants.defaultColumns.value.has(cur.name as any) }), {}) as
        | Record<AgentsTableWindowsColumns, boolean>
        | Record<AgentsTableLinuxColumns, boolean>,
      columnsOrder: viewableColumns,
    };
  });

  const containsPersistentDataTableKeys = (persistentData: object) =>
    Object.prototype.hasOwnProperty.call(persistentData, 'columnsOrder') && Object.prototype.hasOwnProperty.call(persistentData, 'columnsFilter');

  const tableGetEditorData = computed(() => {
    const tableEditor = agentsPagePersistentData?.getTableEditor.value;
    if (containsPersistentDataTableKeys(tableEditor) && tableEditor.columnsOrder.length > 0 && tableEditor.columnsFilter) {
      return tableEditor;
    }
    return defaultColumnsData.value;
  });

  const tableEditor: TableEditor = {
    get: {
      linux: tableGetEditorData,
      mac: tableGetEditorData,
      windows: tableGetEditorData,
    },
    set: {
      linux: value => {
        agentsPagePersistentData?.setTableEditor(value);
      },
      mac: value => {
        agentsPagePersistentData?.setTableEditor(value);
      },
      windows: value => {
        agentsPagePersistentData?.setTableEditor(value);
      },
    },
  };
  const tableColumns = {
    windows: computed(() => tableEditor.get.windows.value.columnsOrder.filter(column => tableEditor.get.windows.value.columnsFilter[column.name])),
    linux: computed(() => tableEditor.get.linux.value.columnsOrder.filter(column => tableEditor.get.linux.value.columnsFilter[column.name])),
    mac: computed(() => tableEditor.get.linux.value.columnsOrder.filter(column => tableEditor.get.linux.value.columnsFilter[column.name])),
  };

  const resetColumnsToDefault = () => {
    if (osService?.isUnix.value) {
      tableEditor.set.linux(defaultColumnsData.value as AgentsPagePersistentDataType['tableEditor']['linux']);
    }
    if (osService?.isWindows.value) {
      tableEditor.set.windows(defaultColumnsData.value as AgentsPagePersistentDataType['tableEditor']['windows']);
    }
  };

  const payload = { tableColumns, tableEditor, resetColumnsToDefault };
  app.provide(IKAgentsColumnsManagement, payload);
  return payload;
};

export const useAgentsColumnsManagement = () => inject(IKAgentsColumnsManagement);

export const useAgentsQueryFilterProvider = () => {
  const osService = useOsService();
  const agentsPagePersistentData = inject(IKAgentsPersistentData);

  const editTable = useAgentsColumnsManagement()?.tableEditor;
  const os = osService?.osType;

  const searchByColumnsFilter = computed(() => {
    const windowsColumns = {
      [Agent_SearchColumns.AgentName]: true,
      [Agent_SearchColumns.BuildTitle]: true,
      [Agent_SearchColumns.BuildGroup]: !!editTable?.get.windows.value.columnsFilter['Build Group'],
      [Agent_SearchColumns.HostComment]: !!editTable?.get.windows.value.columnsFilter['Agent Description'],
      [Agent_SearchColumns.Ip]: !!editTable?.get.windows.value.columnsFilter.IP,
      [Agent_SearchColumns.RoutingIp]: !!editTable?.get.windows.value.columnsFilter['Routing IP'],
      [Agent_SearchColumns.Status]: true,
      [Agent_SearchColumns.MacAddress]: !!editTable?.get.windows.value.columnsFilter['MAC Address'],
      [Agent_SearchColumns.LoggedUsers]: !!editTable?.get.windows.value.columnsFilter['Logged On User'],
    };
    const linuxColumns = {
      [Agent_SearchColumns.AgentName]: true,
      [Agent_SearchColumns.BuildTitle]: true,
      [Agent_SearchColumns.BuildGroup]: true,
      [Agent_SearchColumns.HostComment]: !!editTable?.get.windows.value.columnsFilter['Agent Description'],
      [Agent_SearchColumns.Ip]: true,
      [Agent_SearchColumns.RoutingIp]: true,
      [Agent_SearchColumns.Status]: true,
      [Agent_SearchColumns.MacAddress]: true,
      [Agent_SearchColumns.LoggedUsers]: true,
    };
    const searchByColumns: {
      [OsType.Windows]: Record<Agent_SearchColumns, boolean>;
      [OsType.Linux]: Record<Agent_SearchColumns, boolean>;
      [OsType.Mac]: Record<Agent_SearchColumns, boolean>;
    } = {
      [OsType.Windows]: windowsColumns,
      [OsType.Linux]: linuxColumns,
      [OsType.Mac]: linuxColumns,
    };
    return os?.value ? Object.entries(searchByColumns[os?.value]).flatMap(([key, value]) => (value ? [key as Agent_SearchColumns] : [])) : [];
  });
  const filterState = ref<Agent_WhereInput & Pick<NoUndefinedField<Agent_WhereInput>, 'sort' | 'paging'>>({
    sort: {
      field: Agent_SortField.Name,
      order: Agent_SortOrder.Ascending,
    },
    paging: {
      pageNumber: 1,
      pageSize: 50,
    },
  });

  const agentsQueryFilter = {
    get: computed(() => ({
      ...filterState.value,
      sort: {
        field: agentsPagePersistentData?.getSorting.value?.sort?.field || filterState.value.sort.field,
        order: agentsPagePersistentData?.getSorting.value?.sort?.order || filterState.value.sort.order,
      },
      searchByColumns: searchByColumnsFilter.value,
      paging: {
        ...filterState.value.paging,
        pageSize: agentsPagePersistentData?.getPaging.value || filterState.value.paging.pageSize,
      },
    })),
    set(newValue: typeof filterState.value) {
      filterState.value = { ...newValue, searchByColumns: searchByColumnsFilter.value };
    },
    clear() {
      filterState.value = {
        sort: {
          field: Agent_SortField.Name,
          order: Agent_SortOrder.Ascending,
        },
        paging: {
          pageNumber: 1,
          pageSize: 50,
        },
        searchByColumns: searchByColumnsFilter.value,
      };
    },
  };

  app.provide(IKAgentsQueryFilter, agentsQueryFilter);
  return { agentsQueryFilter };
};

export const useAgentsActions = () => {
  const { mutate: updateAgent } = useAgents_Action_AsyncMutation({});
  const { mutate: updateBuildCacheSettings } = useAgents_BuildCache_Update_AsyncMutation({});
  const { mutate: updateAgentLicense } = useLicense_RegisterAgents_AsyncMutation({});
  const { mutate: updateAgentAllocateBuildCache } = useLicense_RegisterBuildCache_AsyncMutation({});
  const { mutate: updateAddBuildCacheClusterNodes } = useCluster_Add_NodesMutation({});
  const { mutate: updateDeleteBuildCacheClusterNodes } = useCluster_Remove_NodesMutation({});
  const setAgentsAction = async (type: Action_Type, data: { agents: { agentId: Scalars['String'] }[]; parameter: Scalars['AgentAction_Parameter']; key?: Scalars['AgentAction_Key'] }) => {
    const id = await updateAgent({
      data: {
        action: type,
        ...data,
      },
    });
    return longPolling.fetch('agents_action_asyncResult', id?.data?.agents_action_async);
  };
  const setAgentLicense = async (data: License_RegisterAgentRequest[]) => {
    const id = await updateAgentLicense({ data });
    return longPolling.fetch('license_registerAgents_asyncResult', id?.data?.license_registerAgents_async);
  };

  const setAllocateBuildCache = async (data: License_RegisterBuildCacheRequest[]) => {
    const id = await updateAgentAllocateBuildCache({ data });
    return longPolling.fetch('license_registerBuildCache_asyncResult', id?.data?.license_registerBuildCache_async);
  };
  const setBuildCacheSettings = async (data: Agent_BuildCacheSettingsInput) => {
    const id = await updateBuildCacheSettings({ data });
    return longPolling.fetch('agents_buildCache_update_asyncResult', id?.data?.agents_buildCache_update_async);
  };

  const setAddBuildCacheClusterNodes = async (data: Cluster_Nodes_AddInput) => updateAddBuildCacheClusterNodes({ data });
  const setRemoveBuildCacheClusterNodes = async (data: Cluster_Nodes_RemoveInput) => updateDeleteBuildCacheClusterNodes({ data });

  return { setAgentLicense, setBuildCacheSettings, setAllocateBuildCache, setAgentsAction, setAddBuildCacheClusterNodes, setRemoveBuildCacheClusterNodes };
};
